import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMY, KIND_LABEL } from "@/lib/date";
import StatusBadge from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, MagnifyingGlass, Warning, Clock, CheckCircle, FilePdf, Coins } from "@phosphor-icons/react";

const CONFIG = {
  submitted: {
    title: "Submitted this year",
    subtitle: "All Annual Return, Audit send-in and Audited Report filings submitted in the current calendar year.",
    icon: CheckCircle,
    accent: "text-[#10B981]",
    bg: "bg-[#10B981]",
  },
  upcoming: {
    title: "Due within 30 days",
    subtitle: "Deadlines coming due in the next 30 days across all your clients.",
    icon: Clock,
    accent: "text-[#F59E0B]",
    bg: "bg-[#F59E0B]",
  },
  overdue: {
    title: "Overdue",
    subtitle: "Filings past their statutory deadline. Requires immediate action.",
    icon: Warning,
    accent: "text-[#DA1E28]",
    bg: "bg-[#3B82F6]",
  },
  credits: {
    title: "Available Credits",
    subtitle: "Credit balance currently held by each user account in this tenant.",
    icon: Coins,
    accent: "text-[#1E6BC6]",
    bg: "bg-[#1E6BC6]",
  },
  credit_history: {
    title: "Credit Top-Up History",
    subtitle: "Every credit top-up granted to your account, most recent first.",
    icon: Coins,
    accent: "text-[#1E6BC6]",
    bg: "bg-[#1E6BC6]",
  },
};

export default function DashboardList() {
  const [params] = useSearchParams();
  const type = params.get("type") || "upcoming";
  const cfg = CONFIG[type] || CONFIG.upcoming;
  const Icon = cfg.icon;
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    setQ("");
    (async () => {
      try {
        if (type === "submitted") {
          const r = await api.get("/dashboard/submitted-this-year");
          setItems(r.data || []);
        } else if (type === "credits") {
          const r = await api.get("/dashboard/user-credits");
          setItems(r.data || []);
        } else if (type === "credit_history") {
          const r = await api.get("/dashboard/my-credit-history");
          setItems(r.data || []);
        } else {
          const r = await api.get("/dashboard/alerts?window=30");
          const all = r.data || [];
          if (type === "overdue") setItems(all.filter(a => a.status === "overdue"));
          else setItems(all.filter(a => a.status !== "overdue" && a.days_until <= 30));
        }
      } finally { setLoading(false); }
    })();
  }, [type]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(r => {
      const bag = [
        r.client_name, r.ssm_no,
        r.kind_label, KIND_LABEL[r.kind] || "",
        r.email, r.name, r.role,
        r.granted_by, String(r.credits ?? ""),
      ].join(" ").toLowerCase();
      return bag.includes(needle);
    });
  }, [items, q]);

  return (
    <div className="p-8 max-w-[1400px]">
      <button onClick={() => navigate("/")} data-testid="deadlines-back"
              className="inline-flex items-center gap-1 text-sm text-[#6B7280] hover:text-[#111827] mb-4">
        <ArrowLeft size={14} weight="bold" /> Back to dashboard
      </button>
      <header className="mb-6 flex items-start gap-4">
        <div className={`h-12 w-12 rounded-full flex items-center justify-center shrink-0 ${cfg.bg}`}>
          <Icon size={22} weight="duotone" className="text-white" />
        </div>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-[#111827]">
            {cfg.title} <span className="text-[#9CA3AF] font-normal">· {loading ? "—" : items.length}</span>
          </h1>
          <p className="mt-1 text-sm text-[#6B7280] max-w-3xl">{cfg.subtitle}</p>
        </div>
      </header>

      <div className="mb-4 flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input value={q} onChange={e => setQ(e.target.value)}
                 placeholder={
                   type === "credits" ? "Search by name, email or role…"
                   : type === "credit_history" ? "Search by admin or amount…"
                   : "Search by client, SSM or filing…"
                 }
                 className="pl-9 rounded-md h-10" data-testid="deadlines-search" />
        </div>
        <span className="text-xs text-[#6B7280]">Showing {filtered.length} of {items.length}</span>
        <div className="ml-auto">
          <Button
            type="button"
            variant="outline"
            className="rounded-md h-10 gap-2"
            data-testid="deadlines-generate-pdf"
            disabled={loading}
            onClick={() => {
              const token = localStorage.getItem("cosec_token");
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/dashboard/deadlines/pdf?kind=${type}&auth=${encodeURIComponent(token || "")}`;
              window.open(url, "_blank");
            }}>
            <FilePdf size={16} weight="bold" /> Generate PDF
          </Button>
        </div>
      </div>

      <section className="bg-white rounded-lg shadow-sm border border-[#E5E7EB]">
        {loading ? (
          <div className="p-10 text-center text-sm text-[#6B7280]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#6B7280]">
            {items.length === 0 ? "Nothing to show." : "No matches for your search."}
          </div>
        ) : type === "submitted" ? (
          <SubmittedTable items={filtered} />
        ) : type === "credits" ? (
          <CreditsTable items={filtered} />
        ) : type === "credit_history" ? (
          <CreditHistoryTable items={filtered} />
        ) : (
          <DeadlinesTable items={filtered} overdueStyle={type === "overdue"} />
        )}
      </section>
    </div>
  );
}

function SubmittedTable({ items }) {
  return (
    <table className="w-full text-sm" data-testid="submitted-table">
      <thead>
        <tr className="text-left border-b border-border">
          <Th>Client</Th><Th>SSM</Th><Th>Filing</Th><Th>Submitted On</Th><Th>FY End</Th><Th></Th>
        </tr>
      </thead>
      <tbody>
        {items.map((r, i) => (
          <tr key={i} className="data-row border-b border-border/60" data-testid={`submitted-row-${i}`}>
            <td className="px-6 py-3">{r.client_name}</td>
            <td className="px-6 py-3 font-mono text-xs">{r.ssm_no || "—"}</td>
            <td className="px-6 py-3">{r.kind_label}</td>
            <td className="px-6 py-3 font-mono">{fmtMY(r.submitted_date)}</td>
            <td className="px-6 py-3 font-mono">{fmtMY(r.fy_end) || "—"}</td>
            <td className="px-6 py-3 text-right">
              <Link to={`/clients/${r.client_id}`} className="text-xs font-mono uppercase tracking-widest underline">
                Open
              </Link>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DeadlinesTable({ items, overdueStyle }) {
  return (
    <table className="w-full text-sm" data-testid="deadlines-table">
      <thead>
        <tr className="text-left border-b border-border">
          <Th>Client</Th><Th>SSM</Th><Th>Filing</Th><Th>Anniversary</Th>
          <Th>Due Date</Th><Th>Balance</Th><Th>Status</Th><Th></Th>
        </tr>
      </thead>
      <tbody>
        {items.map((a, i) => {
          const bal = a.days_until;
          return (
            <tr key={i}
                className={`data-row border-b border-border ${overdueStyle ? "border-l-4 border-l-[#DA1E28]" : ""}`}
                data-testid={`deadline-row-${i}`}>
              <td className="px-6 py-3">{a.client_name}</td>
              <td className="px-6 py-3 font-mono text-xs">{a.ssm_no}</td>
              <td className="px-6 py-3">{KIND_LABEL[a.kind]}</td>
              <td className="px-6 py-3 font-mono">{fmtMY(a.anniversary_date)}</td>
              <td className="px-6 py-3 font-mono">{fmtMY(a.due_date)}</td>
              <td className="px-6 py-3 font-mono">
                {bal < 0 ? (
                  <span className="text-[#DA1E28] font-medium">{Math.abs(bal)}d overdue</span>
                ) : bal === 0 ? (
                  <span className="text-[#DA1E28] font-medium">Due today</span>
                ) : (
                  <span className={bal <= 7 ? "text-[#F1C21B] font-medium" : "text-foreground"}>
                    {bal}d left
                  </span>
                )}
              </td>
              <td className="px-6 py-3"><StatusBadge status={a.status} /></td>
              <td className="px-6 py-3 text-right">
                <Link to={`/clients/${a.client_id}`} className="text-xs font-mono uppercase tracking-widest underline">
                  Open
                </Link>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const Th = ({ children }) =>
  <th className="px-6 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-normal">{children}</th>;

function CreditHistoryTable({ items }) {
  const total = items.reduce((n, g) => n + (Number(g.credits) || 0), 0);
  return (
    <table className="w-full text-sm" data-testid="credit-history-table">
      <thead>
        <tr className="text-left border-b border-border">
          <Th>Date</Th><Th>Credits Added</Th><Th>Granted By</Th>
          <Th><span className="block text-right">Balance After</span></Th>
        </tr>
      </thead>
      <tbody>
        {items.map((g, i) => (
          <tr key={g.id || i} className="data-row border-b border-border/60"
              data-testid={`credit-history-row-${i}`}>
            <td className="px-6 py-3 font-mono">{fmtMY(g.granted_at)}</td>
            <td className="px-6 py-3">
              <span className="text-[13px] font-mono font-bold text-[#166534]">
                +{Number(g.credits || 0).toLocaleString()}
              </span>
            </td>
            <td className="px-6 py-3 font-mono text-xs">{g.granted_by || "—"}</td>
            <td className="px-6 py-3 text-right font-mono">{Number(g.new_balance || 0).toLocaleString()}</td>
          </tr>
        ))}
        <tr className="bg-muted/20 font-bold">
          <td className="px-6 py-3 font-mono text-[10px] uppercase tracking-wider">Total granted</td>
          <td className="px-6 py-3 font-mono text-[#166534]">+{total.toLocaleString()}</td>
          <td colSpan={2}></td>
        </tr>
      </tbody>
    </table>
  );
}

function CreditsTable({ items }) {
  const total = items.reduce((n, u) => n + (Number(u.credits) || 0), 0);
  return (
    <table className="w-full text-sm" data-testid="credits-table">
      <thead>
        <tr className="text-left border-b border-border">
          <Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Status</Th>
          <Th><span className="block text-right">Credits</span></Th>
        </tr>
      </thead>
      <tbody>
        {items.map((u, i) => (
          <tr key={u.user_id || i} className="data-row border-b border-border/60"
              data-testid={`credits-row-${i}`}>
            <td className="px-6 py-3">{u.name || <span className="text-[#9CA3AF]">—</span>}</td>
            <td className="px-6 py-3 font-mono text-xs">{u.email}</td>
            <td className="px-6 py-3">
              <span className={`text-[11px] font-mono uppercase tracking-wider px-2 py-1 rounded ${u.role === "admin" ? "bg-[#DBEAFE] text-[#1E40AF]" : "bg-[#F3F4F6] text-[#374151]"}`}>
                {u.role}
              </span>
            </td>
            <td className="px-6 py-3">
              {u.disabled
                ? <span className="text-[11px] font-mono uppercase tracking-wider px-2 py-1 rounded bg-[#FEE2E2] text-[#B91C1C]">Disabled</span>
                : <span className="text-[11px] font-mono uppercase tracking-wider px-2 py-1 rounded bg-[#DCFCE7] text-[#166534]">Active</span>}
            </td>
            <td className="px-6 py-3 text-right font-mono font-bold text-[#111827]">
              {u.role === "admin" ? "∞" : Number(u.credits || 0).toLocaleString()}
            </td>
          </tr>
        ))}
        <tr className="bg-muted/20 font-bold">
          <td className="px-6 py-3 font-mono text-[10px] uppercase tracking-wider" colSpan={4}>
            Total credits held (non-admin)
          </td>
          <td className="px-6 py-3 text-right font-mono">{total.toLocaleString()}</td>
        </tr>
      </tbody>
    </table>
  );
}
